import React, { useState, useRef, useEffect } from "react";
import { Copy, Check, RefreshCw, Pencil, User as UserIcon, X, Bookmark, FileText, Image as ImageIcon, Volume2, Loader2 } from "lucide-react";
import StellaLogo from "./StellaLogo";
import Markdown from "./Markdown";
import IntentChip from "./IntentChip";
import { Textarea } from "./ui/textarea";
import { Button } from "./ui/button";
import { toast } from "sonner";
import api, { API_BASE, errText } from "../lib/api";

export default function MessageList({
  messages, streaming, streamingText, streamingStatus, streamingChip,
  brainState,
  onRegenerate, onEditSubmit, onRefresh,
  ttsEnabled = false, voiceStatus = null,
}) {
  const lastPlayedRef = useRef(null);
  const audioRef = useRef(null);
  useEffect(() => () => { try { audioRef.current?.pause(); } catch {} }, []);
  useEffect(() => {
    // When streaming just ended and ttsEnabled is on, auto-play the last assistant message
    if (streaming || !ttsEnabled || !messages?.length) return;
    const last = messages[messages.length - 1];
    if (!last || last.role !== "assistant") return;
    if (lastPlayedRef.current === last.id) return;
    lastPlayedRef.current = last.id;
    // Trigger auto-play using our helper via a synthetic hook.
    playTTS(last.content, { managed: !!voiceStatus?.tts?.available, silent: true, audioRef });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streaming, messages, ttsEnabled, voiceStatus]);

  return (
    <div className="w-full max-w-3xl mx-auto px-3 sm:px-5 py-6 space-y-6" data-testid="messages-list">
      {messages.map((m, idx) => (
        <MessageBubble
          key={m.id}
          message={m}
          onEditSubmit={onEditSubmit}
          onRegenerate={onRegenerate}
          onRefresh={onRefresh}
          isLastAssistant={m.role === "assistant" && idx === messages.length - 1}
          hideActions={streaming && idx === messages.length - 1}
          voiceStatus={voiceStatus}
          sharedAudioRef={audioRef}
        />
      ))}
      {streaming && (
        <div className="flex gap-3" data-testid="streaming-bubble">
          <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center shrink-0">
            <StellaLogo className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            {brainState?.agents?.length > 0 && (
              <div className="mb-2 rounded-xl border border-indigo-200 bg-indigo-50/70 px-3 py-2 text-[11px] text-indigo-800"
                   data-testid="brain-status-panel">
                <div className="font-semibold flex items-center gap-1">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse" />
                  Using Stella Brain
                  {brainState.escalated && (
                    <span className="ml-1 text-[9px] uppercase tracking-wider bg-white/70 text-indigo-700 border border-indigo-200 rounded-full px-1.5 py-0.5">
                      auto-escalated
                    </span>
                  )}
                </div>
                <div className="mt-0.5 text-slate-600">
                  Agents: {brainState.agents.join(" · ")}
                  {brainState.risk_tier && <span className="ml-1 text-slate-400">· risk {brainState.risk_tier}</span>}
                </div>
                {brainState.review && (
                  <div className="mt-1 text-slate-600 italic" data-testid="brain-review-line">
                    {brainState.review}
                  </div>
                )}
              </div>
            )}
            {streamingStatus && (
              <div className="text-[11px] font-medium uppercase tracking-wider text-indigo-700 mb-2" data-testid="streaming-status">
                {streamingStatus}
              </div>
            )}
            {streamingChip && <IntentChip chip={streamingChip} onChanged={onRefresh} />}
            {streamingText ? (
              <Markdown content={streamingText} />
            ) : (
              <div className="inline-flex items-center gap-1 text-slate-400">
                <Dot /> <Dot delay="150ms" /> <Dot delay="300ms" />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Play TTS: prefer managed backend; fall back to browser speechSynthesis.
async function playTTS(text, { managed = false, silent = false, audioRef } = {}) {
  if (!text) return;
  try {
    // Stop any current playback
    try { audioRef?.current?.pause(); } catch {}
    if (typeof window !== "undefined") {
      try { window.speechSynthesis?.cancel?.(); } catch {}
    }
    if (managed) {
      const res = await fetch(`${API_BASE}/voice/speak`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: text.slice(0, 3000), response_format: "mp3", voice: "alloy" }),
      });
      if (!res.ok) throw new Error(String(res.status));
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      if (audioRef) audioRef.current = audio;
      audio.onended = () => URL.revokeObjectURL(url);
      audio.onerror = () => URL.revokeObjectURL(url);
      await audio.play();
      return;
    }
    // Fallback: browser speechSynthesis
    if (typeof window !== "undefined" && "speechSynthesis" in window) {
      const u = new SpeechSynthesisUtterance(text.slice(0, 3000));
      u.rate = 1.0; u.pitch = 1.0;
      window.speechSynthesis.speak(u);
      return;
    }
    if (!silent) toast.message("Voice not available yet");
  } catch (err) {
    if (!silent) toast.error("Playback failed");
  }
}

function Dot({ delay = "0ms" }) {
  return (
    <span
      className="w-1.5 h-1.5 rounded-full bg-indigo-400 inline-block animate-pulse"
      style={{ animationDelay: delay }}
    />
  );
}

function MessageBubble({ message, onEditSubmit, onRegenerate, onRefresh, isLastAssistant, hideActions, voiceStatus, sharedAudioRef }) {
  const isUser = message.role === "user";
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(message.content);
  const [remembered, setRemembered] = useState(false);
  const [ttsBusy, setTtsBusy] = useState(false);
  const managedTtsOn = !!voiceStatus?.tts?.available;
  const speechSynthOn = typeof window !== "undefined" && "speechSynthesis" in window;
  const confidential = !!voiceStatus?.confidential_mode;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch {}
  };

  const rememberThis = async () => {
    if (remembered) return;
    try {
      await api.post("/memories", {
        type: "user_preference",
        content: message.content.slice(0, 900),
        source: "chat",
        context_scope: "general",
      });
      setRemembered(true);
      toast.success("Saved to Memory");
      onRefresh?.();
    } catch (err) {
      if (err?.response?.status === 409) {
        toast.message("Looks sensitive", { description: "Open Memory to confirm and save." });
      } else {
        toast.error(errText(err, "Could not save"));
      }
    }
  };

  const submitEdit = () => {
    onEditSubmit(message.id, editValue.trim());
    setEditing(false);
  };

  return (
    <div className={`flex gap-3 ${isUser ? "flex-row-reverse" : ""}`} data-testid={`message-${message.id}`}>
      {isUser ? (
        <div className="w-8 h-8 rounded-full bg-slate-900 text-white flex items-center justify-center shrink-0">
          <UserIcon className="w-4 h-4" />
        </div>
      ) : (
        <div className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center shrink-0">
          <StellaLogo className="w-4 h-4" />
        </div>
      )}
      <div className={`flex-1 min-w-0 ${isUser ? "flex flex-col items-end" : ""}`}>
        {editing ? (
          <div className="w-full max-w-full bg-white border border-indigo-200 rounded-xl p-2">
            <Textarea
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className="min-h-[80px]"
              data-testid="edit-textarea"
            />
            <div className="flex gap-2 justify-end mt-2">
              <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>
                <X className="w-3.5 h-3.5 mr-1" /> Cancel
              </Button>
              <Button size="sm" className="bg-indigo-700 hover:bg-indigo-800 text-white" onClick={submitEdit} data-testid="edit-submit">
                Resend
              </Button>
            </div>
          </div>
        ) : isUser ? (
          <div className="max-w-full">
            {message.meta?.attachments?.length > 0 && (
              <AttachmentChips atts={message.meta.attachments} />
            )}
            <div className="bg-indigo-50 text-slate-900 rounded-2xl rounded-tr-md px-4 py-3 text-sm sm:text-base whitespace-pre-wrap break-words">
              {message.content}
            </div>
          </div>
        ) : (
          <>
            <Markdown content={message.content} />
            {message.meta?.chip && (
              <IntentChip chip={message.meta.chip} onChanged={onRefresh} />
            )}
          </>
        )}

        {!editing && !hideActions && (
          <div className={`flex items-center gap-1 mt-1 ${isUser ? "justify-end" : ""}`}>
            <button
              onClick={copy}
              className="p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100"
              aria-label="Copy message"
              data-testid={`msg-copy-${message.id}`}
            >
              {copied ? <Check className="w-3.5 h-3.5 text-emerald-600" /> : <Copy className="w-3.5 h-3.5" />}
            </button>
            {isUser && (
              <button
                onClick={() => setEditing(true)}
                className="p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                aria-label="Edit message"
                data-testid={`msg-edit-${message.id}`}
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            )}
            {!isUser && (
              <button
                onClick={rememberThis}
                disabled={remembered}
                className="p-1.5 rounded-md text-slate-400 hover:text-indigo-700 hover:bg-slate-100 disabled:opacity-50"
                aria-label="Remember this"
                data-testid={`msg-remember-${message.id}`}
              >
                <Bookmark className={`w-3.5 h-3.5 ${remembered ? "fill-indigo-500 text-indigo-500" : ""}`} />
              </button>
            )}
            {!isUser && isLastAssistant && (
              <button
                onClick={onRegenerate}
                className="p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                aria-label="Regenerate response"
                data-testid={`msg-regenerate-${message.id}`}
              >
                <RefreshCw className="w-3.5 h-3.5" />
              </button>
            )}
            {!isUser && !confidential && (managedTtsOn || speechSynthOn) && (
              <button
                onClick={async () => {
                  if (ttsBusy) return;
                  setTtsBusy(true);
                  try {
                    await playTTS(message.content, { managed: managedTtsOn, audioRef: sharedAudioRef });
                  } finally {
                    setTtsBusy(false);
                  }
                }}
                className="p-1.5 rounded-md text-slate-400 hover:text-indigo-700 hover:bg-slate-100"
                aria-label="Listen to response"
                data-testid={`msg-tts-${message.id}`}
                disabled={ttsBusy}
              >
                {ttsBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Volume2 className="w-3.5 h-3.5" />}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function AttachmentChips({ atts }) {
  if (!atts?.length) return null;
  const images = atts.filter((a) => (a.mime_type || "").startsWith("image/"));
  const others = atts.filter((a) => !(a.mime_type || "").startsWith("image/"));
  return (
    <div className="flex flex-col gap-1.5 mb-1.5 items-end" data-testid="msg-attachments">
      {images.length > 0 && (
        <div className="flex flex-wrap gap-1.5 justify-end">
          {images.map((a) => (
            <a key={a.id} href={`${API_BASE}/files/${a.id}/download`}
               target="_blank" rel="noopener noreferrer"
               className="block max-w-[220px] rounded-xl overflow-hidden border border-[#E5E4DE] bg-white"
               data-testid={`msg-attachment-image-${a.id}`}>
              <img
                src={`${API_BASE}/files/${a.id}/download`}
                alt={a.filename}
                className="block max-h-40 w-auto object-contain"
                loading="lazy"
              />
              <div className="px-2 py-1 text-[11px] text-slate-500 truncate">{a.filename}</div>
            </a>
          ))}
        </div>
      )}
      {others.length > 0 && (
        <div className="flex flex-wrap gap-1.5 justify-end">
          {others.map((a) => (
            <a key={a.id} href={`${API_BASE}/files/${a.id}/download`}
               target="_blank" rel="noopener noreferrer"
               className="inline-flex items-center gap-1.5 bg-white border border-[#E5E4DE] rounded-full pl-2 pr-3 py-1 text-[11px] text-slate-700 hover:border-indigo-300"
               data-testid={`msg-attachment-file-${a.id}`}>
              <FileText className="w-3 h-3 text-indigo-500" />
              <span className="truncate max-w-[220px]">{a.filename}</span>
              {!a.text_extracted && a.mime_type?.startsWith("image/") === false && (
                <span className="text-[10px] text-slate-400">(no text)</span>
              )}
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
